import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Table, Nav, Tab, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Line, Pie, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  Filler,
} from 'chart.js';
import { dsoApi } from '../../services/api';
import { 
  FaRupeeSign, 
  FaPercentage, 
  FaUsers, 
  FaStar, 
  FaArrowUp, 
  FaArrowDown,
  FaChartBar,
  FaChartPie,
  FaUserFriends,
  FaCreditCard,
  FaInfoCircle,
  FaFileInvoiceDollar
} from 'react-icons/fa';
import './Earnings.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  Filler
);

// Helper function to format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '₹0';
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// Helper function to format percentages
const formatPercentage = (value) => {
  if (value === null || value === undefined) return '0%';
  return `${value.toFixed(2)}%`;
};

// Gradient helper for charts
const createGradient = (ctx, colorStart, colorEnd) => {
  const gradient = ctx.createLinearGradient(0, 0, 0, 400);
  gradient.addColorStop(0, colorStart);
  gradient.addColorStop(1, colorEnd);
  return gradient;
};

const DSOEarnings = () => {
  const [summaryStats, setSummaryStats] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [agentEarnings, setAgentEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Default to overview tab since we removed products tab
  const [activeKey, setActiveKey] = useState('overview');

  useEffect(() => {
    document.title = 'DSO Earnings Dashboard';

    const fetchData = async () => {
      setLoading(true);
      setError('');
      
      try {
        // Using allSettled to handle partial API failures gracefully
        const results = await Promise.allSettled([
          dsoApi.getEarningsSummary(),
          dsoApi.getMonthlyEarnings(),
          dsoApi.getEarningsByCategory(),
          dsoApi.getAgentEarnings(),
        ]);
        
        // Extract data from fulfilled promises or set defaults for rejected ones
        const [summaryRes, monthlyRes, categoryRes, agentsRes] = results;
        
        // Summary Stats
        if (summaryRes.status === 'fulfilled') {
          setSummaryStats(summaryRes.value.data);
        } else {
          console.error('Error fetching summary stats:', summaryRes.reason);
          setSummaryStats({
            total_earnings: 0,
            pending_payouts: 0,
            average_commission: 0,
            top_category_name: 'N/A'
          });
        }
        
        // Monthly Data
        if (monthlyRes.status === 'fulfilled') {
          setMonthlyData(monthlyRes.value.data);
        } else {
          console.error('Error fetching monthly data:', monthlyRes.reason);
          setMonthlyData({
            labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
            datasets: [{ data: [0, 0, 0, 0, 0, 0] }]
          });
        }
        
        // Category Data
        if (categoryRes.status === 'fulfilled') {
          setCategoryData(categoryRes.value.data);
        } else {
          console.error('Error fetching category data:', categoryRes.reason);
          setCategoryData([]);
        }
        
        // Agent Earnings
        if (agentsRes.status === 'fulfilled') {
          const agentsData = agentsRes.value.data;
          if (Array.isArray(agentsData)) {
            setAgentEarnings(agentsData);
          } else {
            setAgentEarnings(Array.isArray(agentsData?.items) ? agentsData.items : []);
          }
        } else {
          console.error('Error fetching agent earnings:', agentsRes.reason);
          setAgentEarnings([]);
        }
        
        // Check if all APIs failed and set an error if so
        const allFailed = results.every(result => result.status === 'rejected');
        if (allFailed) {
          setError('Could not load any earnings data. Please check your connection and try again.');
        }

      } catch (err) {
        console.error("Error fetching earnings data:", err);
        setError(err.response?.data?.detail || 'Failed to load earnings data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // --- Chart Options & Data ---

  // Monthly Trend Line Chart
  // Convert the function to a usable object for react-chartjs-2 v4+
  const getMonthlyChartData = {
    labels: monthlyData?.labels || [],
    datasets: [
      {
        label: 'Monthly Earnings',
        data: monthlyData?.datasets?.[0]?.data || [],
        borderColor: '#3b82f6',
        borderWidth: 2,
        backgroundColor: 'rgba(59, 130, 246, 0.2)',  // Simple background color instead of gradient
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#3b82f6',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: '#3b82f6',
        pointHoverBorderWidth: 2,
        pointHoverBorderColor: '#fff',
      },
    ],
  };

  const monthlyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(17, 25, 40, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        cornerRadius: 8,
        boxPadding: 6,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            return ` Earnings: ${formatCurrency(context.parsed.y)}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 12,
            weight: '500',
          },
          color: '#64748b',
          padding: 8,
        }
      },
      y: {
        beginAtZero: true,
        border: {
          display: false,
        },
        grid: {
          color: '#f1f5f9',
        },
        ticks: {
          font: {
            size: 12,
            weight: '500',
          },
          color: '#64748b',
          padding: 8,
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    elements: {
      line: {
        borderWidth: 2,
      }
    }
  };

  // Category Doughnut Chart
  const categoryChartData = {
    labels: categoryData?.map(item => item?.category_name || 'Unknown') || [],
    datasets: [
      {
        data: categoryData?.map(item => item?.total_amount || 0) || [],
        backgroundColor: [
          '#3b82f6', // Blue
          '#6366f1', // Indigo
          '#8b5cf6', // Purple
          '#ec4899', // Pink
          '#f43f5e', // Rose
          '#f97316', // Orange
          '#f59e0b', // Amber
          '#10b981', // Emerald
          '#06b6d4', // Cyan
          '#0ea5e9', // Light Blue
        ],
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverOffset: 8,
      },
    ],
  };

  const categoryChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        position: 'bottom',
        align: 'start',
        labels: {
          padding: 20,
          usePointStyle: true,
          boxWidth: 10,
          boxHeight: 10,
          font: { 
            size: 13,
            weight: '500' 
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(17, 25, 40, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: function(context) {
            const value = context.parsed;
            const total = context.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return ` ${context.label}: ${formatCurrency(value)} (${percentage}%)`;
          }
        }
      }
    },
  };

  // Agent Performance Bar Chart
  const getAgentPerformanceData = () => {
    // Ensure agentEarnings is an array before spreading and sorting
    const validAgentEarnings = Array.isArray(agentEarnings) ? agentEarnings : [];
    const sortedAgents = [...validAgentEarnings].sort((a, b) => b.total_amount - a.total_amount).slice(0, 8);

    return {
      labels: sortedAgents.map(agent => agent?.name || 'Unknown Agent'),
      datasets: [
        {
          label: 'Total Earnings',
          data: sortedAgents.map(agent => agent?.total_amount || 0),
          backgroundColor: '#3b82f6',
          borderRadius: 6,
        }
      ]
    };
  };

  const agentBarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(17, 25, 40, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: function(context) {
            return ` Earnings: ${formatCurrency(context.parsed.x)}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          },
          font: {
            size: 12,
          }
        }
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
          }
        }
      }
    }
  };

  // --- Render Logic ---

  if (loading) {
    return (
      <Container fluid className="d-flex justify-content-center align-items-center earnings-loading">
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3" variant="primary">
            <span className="visually-hidden">Loading Dashboard...</span>
          </Spinner>
          <h5>Loading your earnings dashboard</h5>
          <p className="text-muted">Please wait while we fetch your revenue data...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger" className="earnings-error">
          <Alert.Heading>Error Loading Data</Alert.Heading>
          <p>{error}</p>
          <hr />
          <div className="d-flex justify-content-end">
            <button className="btn btn-outline-danger" onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="earnings-dashboard px-4 pb-5">
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Earnings Dashboard</h1>
          <p className="dashboard-subtitle">Track your revenue performance and agent commissions</p>
        </div>
        <div className="dashboard-actions">
          <div className="period-filter">
            <span className="period-label">Last updated:</span>
            <span className="period-value">{new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      <Tab.Container id="earnings-tabs" activeKey={activeKey} onSelect={setActiveKey}>
        <Nav variant="tabs" className="dashboard-tabs">
          <Nav.Item>
            <Nav.Link eventKey="overview" className="dashboard-tab">
              <FaChartBar className="tab-icon" /> Overview
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="agents" className="dashboard-tab">
              <FaUserFriends className="tab-icon" /> Agents
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content className="mt-4">
          <Tab.Pane eventKey="overview">
            <Row className="mb-4 g-4">
              <Col md={6} lg={3}>
                <Card className="stat-card">
                  <div className="stat-icon-wrapper bg-blue">
                    <FaRupeeSign className="stat-icon" />
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Total Earnings</div>
                    <div className="stat-value">{formatCurrency(summaryStats?.total_earnings)}</div>
                    <div className="stat-trend positive">
                      <FaArrowUp className="trend-icon" /> <span>12.4% from last month</span>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col md={6} lg={3}>
                <Card className="stat-card">
                  <div className="stat-icon-wrapper bg-amber">
                    <FaFileInvoiceDollar className="stat-icon" />
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Pending Payouts</div>
                    <div className="stat-value">{formatCurrency(summaryStats?.pending_payouts)}</div>
                    <div className="stat-trend negative">
                      <FaArrowDown className="trend-icon" /> <span>3.2% from last month</span>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col md={6} lg={3}>
                <Card className="stat-card">
                  <div className="stat-icon-wrapper bg-emerald">
                    <FaPercentage className="stat-icon" />
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Avg. Commission</div>
                    <div className="stat-value">{formatPercentage(summaryStats?.average_commission)}</div>
                    <div className="stat-trend positive">
                      <FaArrowUp className="trend-icon" /> <span>1.8% from last month</span>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col md={6} lg={3}>
                <Card className="stat-card">
                  <div className="stat-icon-wrapper bg-indigo">
                    <FaUsers className="stat-icon" />
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Active Agents</div>
                    <div className="stat-value">{agentEarnings.length}</div>
                    <div className="stat-badge">
                      Top category: <Badge bg="light" text="dark">{summaryStats?.top_category_name || 'N/A'}</Badge>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>

            <Row className="g-4">
              <Col lg={6}>
                <Card className="chart-card h-100">
                  <Card.Header className="chart-header">
                    <h5 className="chart-title">
                      <FaChartBar className="me-2" /> Monthly Earnings Trend
                    </h5>
                    <div className="chart-actions">
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Monthly earnings over the last 6 months</Tooltip>}
                      >
                        <span><FaInfoCircle /></span>
                      </OverlayTrigger>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <div className="chart-container" style={{ height: '350px' }}>
                      {monthlyData && monthlyData.labels?.length > 0 ? (
                        <Line options={monthlyChartOptions} data={getMonthlyChartData} />
                      ) : (
                        <div className="no-data-message">Not enough data for monthly chart</div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={6}>
                <Card className="chart-card h-100">
                  <Card.Header className="chart-header">
                    <h5 className="chart-title">
                      <FaChartPie className="me-2" /> Category Breakdown
                    </h5>
                    <div className="chart-actions">
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Earnings distribution by product category</Tooltip>}
                      >
                        <span><FaInfoCircle /></span>
                      </OverlayTrigger>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <div className="chart-container" style={{ height: '350px', position: 'relative' }}>
                      {categoryData && categoryData.length > 0 ? (
                        <Doughnut options={categoryChartOptions} data={categoryChartData} />
                      ) : (
                        <div className="no-data-message">No category earnings data available</div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>


          <Tab.Pane eventKey="agents">
            <Row className="mb-4">
              <Col>
                <Card className="table-card">
                  <Card.Header className="chart-header">
                    <h5 className="chart-title">Agent Performance</h5>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <table className="table table-agents">
                        <thead className="table-header">
                          <tr>
                            <th>Agent</th>
                            <th>Total Earnings</th>
                            <th>Leads</th>
                            <th>Avg. Commission</th>
                            <th>Top Category</th>
                            <th>Performance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Before the Agent Table JSX: */}
                          {console.log("Rendering Agent Table, agentEarnings:", agentEarnings)}
                          {agentEarnings && agentEarnings.length > 0 ? (
                            agentEarnings
                              .sort((a, b) => (b?.total_amount || 0) - (a?.total_amount || 0))
                              .map((agent, index) => (
                                <tr key={index}>
                                  <td>
                                    <div className="agent-info">
                                      <div className="agent-avatar">
                                        {agent?.profile_image ? (
                                          <img src={agent.profile_image} alt={agent?.name || 'Agent'} />
                                        ) : (
                                          <div className="avatar-placeholder">
                                            {agent?.name?.charAt(0) || '?'}
                                          </div>
                                        )}
                                      </div>
                                      <div className="agent-name">{agent?.name || 'Unknown Agent'}</div>
                                    </div>
                                  </td>
                                  <td>{formatCurrency(agent?.total_amount)}</td>
                                  <td>{agent?.total_leads ?? 0}</td>
                                  <td>{formatPercentage(agent?.avg_commission)}</td>
                                  <td>
                                    {agent?.top_category ? (
                                      <Badge bg="light" text="dark" className="category-badge">
                                        {agent.top_category}
                                      </Badge>
                                    ) : (
                                      '-'
                                    )}
                                  </td>
                                  <td>
                                    <div className="performance-meter" data-value={Math.min(100, ((agent?.total_amount || 0) / 50000) * 100)}>
                                      <div
                                        className="performance-bar"
                                        style={{
                                          width: `${Math.min(100, ((agent?.total_amount || 0) / 50000) * 100)}%`,
                                          backgroundColor:
                                            (agent?.total_amount || 0) > 40000 ? '#10b981' :
                                            (agent?.total_amount || 0) > 20000 ? '#3b82f6' :
                                            (agent?.total_amount || 0) > 10000 ? '#f59e0b' :
                                            '#ef4444'
                                        }}
                                      ></div>
                                    </div>
                                  </td>
                                </tr>
                              ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center py-4">No agent earnings data available</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col>
                <Card className="chart-card">
                  <Card.Header className="chart-header">
                    <h5 className="chart-title">Agent Earnings Comparison</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="chart-container" style={{ height: '500px', position: 'relative' }}>
                      {agentEarnings && agentEarnings.length > 0 ? (
                        <Bar 
                          options={agentBarChartOptions}
                          data={getAgentPerformanceData()}
                        />
                      ) : (
                        <div className="no-data-message">No agent data available</div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </Container>
  );
};

export default DSOEarnings;
